// utils/userIdentificationService.js
import { v4 as uuidv4 } from 'uuid'; // You'll need to install: npm install uuid
import { apiService } from './apiService';

class UserIdentificationService {
  constructor() {
    this.LOCAL_USER_ID_KEY = 'helexpo_user_id';
    this.SESSION_USER_ID_KEY = 'helexpo_session_id';
    this.DEVICE_FINGERPRINT_KEY = 'helexpo_device_fp';
    this.LAST_SEEN_KEY = 'helexpo_last_seen';
    this.USER_SESSIONS_KEY = 'helexpo_user_sessions';
  }

  // Generate a unique device fingerprint for additional identification
  generateDeviceFingerprint() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Device fingerprint', 2, 2);
    
    const fingerprint = {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      platform: navigator.platform,
      userAgent: navigator.userAgent.substring(0, 100), // Truncated for privacy
      canvas: canvas.toDataURL().substring(0, 50), // Truncated
      timestamp: Date.now()
    };
    
    return btoa(JSON.stringify(fingerprint)).substring(0, 32);
  }

  // Get or create persistent user ID
  getPersistentUserId() {
    let userId = localStorage.getItem(this.LOCAL_USER_ID_KEY);
    
    if (!userId) {
      userId = uuidv4();
      localStorage.setItem(this.LOCAL_USER_ID_KEY, userId);
      localStorage.setItem(this.LAST_SEEN_KEY, new Date().toISOString());
      
      // Also store device fingerprint for additional verification
      const fingerprint = this.generateDeviceFingerprint();
      localStorage.setItem(this.DEVICE_FINGERPRINT_KEY, fingerprint);
      
     // console.log('🆔 Generated new persistent user ID:', userId);
    }
    
    return userId;
  }

  // Get or create session ID
  getSessionId() {
    let sessionId = sessionStorage.getItem(this.SESSION_USER_ID_KEY);
    
    if (!sessionId) {
      sessionId = uuidv4();
      sessionStorage.setItem(this.SESSION_USER_ID_KEY, sessionId);
      //console.log('🔄 Generated new session ID:', sessionId);
    }
    
    return sessionId;
  }

  // Get comprehensive user identification
  getUserIdentification() {
    const persistentUserId = this.getPersistentUserId();
    const sessionId = this.getSessionId();
    const deviceFingerprint = localStorage.getItem(this.DEVICE_FINGERPRINT_KEY);
    
    // Update last seen
    localStorage.setItem(this.LAST_SEEN_KEY, new Date().toISOString());
    
    return {
      persistentUserId,
      sessionId,
      deviceFingerprint,
      timestamp: new Date().toISOString(),
      isNewSession: !sessionStorage.getItem('session_initialized'),
      userAgent: navigator.userAgent,
      referrer: document.referrer || 'direct',
      url: window.location.href
    };
  }

  // Track session for analytics
  trackSession() {
    const identification = this.getUserIdentification();
    
    // Mark session as initialized
    sessionStorage.setItem('session_initialized', 'true');
    
    // Store session info for analytics
    let sessions = JSON.parse(localStorage.getItem(this.USER_SESSIONS_KEY) || '[]');
    sessions.push({
      sessionId: identification.sessionId,
      startTime: identification.timestamp,
      isNewUser: identification.isNewSession
    });
    
    // Keep only last 10 sessions
    if (sessions.length > 10) {
      sessions = sessions.slice(-10);
    }
    
    localStorage.setItem(this.USER_SESSIONS_KEY, JSON.stringify(sessions));
    
    return identification;
  }

  // Send identification to backend
  async sendIdentificationToBackend(additionalData = {}) {
     const identification = this.getUserIdentification();
      
      const payload = {
        ...identification,
        ...additionalData,
        appVersion: process.env.REACT_APP_VERSION || '1.0.0',
        platform: 'pwa',
        connectionType: navigator.connection?.effectiveType || 'unknown'
      };

    try {
      //console.log("📡 Sending user identification to backend:", payload)
      // You'll need to add this endpoint to your API
      const response = await apiService.client.post('deviceregistration/pwa', payload);
      
      console.log('✅ User identification sent to backend');
      return response.data;
    } catch (error) {
      console.error('❌ Failed to send user identification:', error);
      
      // Store for retry when online
      this.queueIdentificationForRetry(payload);
      return null;
    }
  }

  // Queue for retry when offline
  queueIdentificationForRetry(payload) {
    const queue = JSON.parse(localStorage.getItem('identification_queue') || '[]');
    queue.push({
      ...payload,
      queuedAt: new Date().toISOString()
    });
    
    // Keep only last 5 items
    if (queue.length > 5) {
      queue.splice(0, queue.length - 5);
    }
    
    localStorage.setItem('identification_queue', JSON.stringify(queue));
  }

  // Retry queued identifications when back online
  async retryQueuedIdentifications() {
    const queue = JSON.parse(localStorage.getItem('identification_queue') || '[]');
    
    if (queue.length === 0) return;
    
    console.log('🔄 Retrying', queue.length, 'queued identifications');
    
    for (const payload of queue) {
      try {
        await apiService.client.post('deviceregistration/pwa', payload);
       // console.log('✅ Retried identification successfully');
      } catch (error) {
        console.error('❌ Failed to retry identification:', error);
        break; // Stop retrying if one fails
      }
    }
    
    // Clear successful retries
    localStorage.removeItem('identification_queue');
  }

  // Initialize user tracking
  async initializeUserTracking(additionalData = {}) {
    //console.log('🚀 Initializing user tracking...');
    
    const identification = this.trackSession();
    
    // Send to backend
    await this.sendIdentificationToBackend(additionalData);
    
    // Set up online event listener for retries
    window.addEventListener('online', () => {
     // console.log('🌐 Back online, retrying queued identifications');
      this.retryQueuedIdentifications();
    });
    
    return identification;
  }

  // Check if user is returning
  isReturningUser() {
    const lastSeen = localStorage.getItem(this.LAST_SEEN_KEY);
    return !!lastSeen;
  }

  // Get user statistics
  getUserStats() {
    const sessions = JSON.parse(localStorage.getItem(this.USER_SESSIONS_KEY) || '[]');
    const lastSeen = localStorage.getItem(this.LAST_SEEN_KEY);
    
    return {
      totalSessions: sessions.length,
      lastSeen: lastSeen ? new Date(lastSeen) : null,
      isReturningUser: this.isReturningUser(),
      persistentUserId: localStorage.getItem(this.LOCAL_USER_ID_KEY),
      currentSessionId: sessionStorage.getItem(this.SESSION_USER_ID_KEY)
    };
  }

  // Clear user data (for privacy/GDPR compliance)
  clearUserData() {
    localStorage.removeItem(this.LOCAL_USER_ID_KEY);
    localStorage.removeItem(this.DEVICE_FINGERPRINT_KEY);
    localStorage.removeItem(this.LAST_SEEN_KEY);
    localStorage.removeItem(this.USER_SESSIONS_KEY);
    localStorage.removeItem('identification_queue');
    sessionStorage.removeItem(this.SESSION_USER_ID_KEY);
    sessionStorage.removeItem('session_initialized');
    
    //console.log('🗑️ User identification data cleared');
  }
}

export const userIdentificationService = new UserIdentificationService();
export default userIdentificationService;




